import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaPrayingHands } from 'react-icons/fa';
import Footer from '../components/layout/Footer';

const { FiBook, FiBookOpen, FiTrendingUp, FiUsers, FiStar } = FiIcons;

const Home = () => {
    const features = [
        {
            title: 'Holy Quran',
            description: 'Read, listen, and study the Quran with multiple translations and tafseer',
            icon: FiBook,
            path: '/quran',
            color: 'from-emerald-500 to-teal-500'
        },
        {
            title: 'Hadith Collection',
            description: 'Explore authentic Hadith from major collections with detailed commentary',
            icon: FiBookOpen,
            path: '/hadith',
            color: 'from-blue-500 to-cyan-500'
        },
        {
            title: 'Islamic Duas',
            description: 'Learn and practice duas from Quran and Sunnah for daily life',
            icon: FaPrayingHands,
            path: '/duas',
            color: 'from-rose-500 to-pink-500'
        },
        {
            title: 'Islamic Books',
            description: 'Access a comprehensive library of Islamic literature and resources',
            icon: FiBook, // Changed to FiBook to reflect books
            path: '/books',
            color: 'from-purple-500 to-indigo-500'
        }
    ];

    const stats = [
        { label: 'Verses', value: '6,236', icon: FiBook },
        { label: 'Hadiths', value: '40,000+', icon: FiBookOpen },
        { label: 'Duas', value: '500+', icon: FaPrayingHands },
        { label: 'Users', value: '100K+', icon: FiUsers }
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative py-20 px-4 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto"
                >
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                        <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                            Islam 101
                        </span>
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                        Your comprehensive Islamic learning platform. Read the Quran, explore Hadith,
                        learn Duas, and access Islamic literature all in one place.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/quran"
                            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors duration-200"
                        >
                            Start Reading Quran
                        </Link>
                        <Link
                            to="/signup"
                            className="px-8 py-3 border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg font-semibold transition-colors duration-200"
                        >
                            Join Our Community
                        </Link>
                    </div>
                </motion.div>

                {/* Decorative Arabic Calligraphy */}
                {/* <div className="absolute top-10 left-10 text-6xl text-emerald-200 dark:text-emerald-800 opacity-20">
                    ﷽
                </div>
                <div className="absolute bottom-10 right-10 text-4xl text-emerald-200 dark:text-emerald-800 opacity-20">
                    الحمد لله
                </div> */}
            </section>

            {/* Features Grid */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            Explore Islamic Knowledge
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                            Discover the beauty of Islam through our comprehensive modules designed for learners of all levels.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.1 * index }}
                                whileHover={{ y: -5 }}
                            >
                                <Link
                                    to={feature.path}
                                    className="block p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
                                >
                                    <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                                        <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        {feature.description}
                                    </p>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 px-4 bg-emerald-50 dark:bg-gray-800">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="grid grid-cols-2 lg:grid-cols-4 gap-8"
                    >
                        {stats.map((stat) => (
                            <div key={stat.label} className="text-center">
                                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
                                </div>
                                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                    {stat.value}
                                </div>
                                <div className="text-gray-600 dark:text-gray-300">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Features Highlight */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-3 gap-12">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="text-center lg:text-left"
                        >
                            <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-emerald-600 mx-auto lg:mx-0 mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                Track Your Progress
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                Monitor your reading progress, maintain streaks, and earn badges as you advance in your Islamic studies.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="text-center"
                        >
                            <SafeIcon icon={FiUsers} className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                Join Community
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                Connect with fellow learners, share insights, and participate in group studies and discussions.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 1.0 }}
                            className="text-center lg:text-right"
                        >
                            <SafeIcon icon={FiStar} className="w-12 h-12 text-emerald-600 mx-auto lg:mx-0 mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                Personalized Experience
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                Customize your learning experience with personalized settings, bookmarks, and study preferences.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Home;